/**
 * syncSettings — debounced PATCH /v1/me/settings client (W3W4 P2-c).
 *
 * Responsibilities:
 *   1. Coalesce rapid toggles into a single PATCH per 1s window (debounce).
 *   2. Enforce a single in-flight request gate — concurrent toggles overwrite
 *      the pending payload and skip the network call until prior PATCH lands.
 *      Mitigates the LWW collision window to ~200ms round-trip + 1s debounce.
 *   3. Soft client-side rate-limit (30/min). After the cap, drops payloads
 *      with a console warning; the server's KV rate-limit (30/min) is the
 *      authoritative guard.
 *   4. Dirty-check skip — caller passes the previously-applied value; if the
 *      pending payload is identical, no PATCH is issued.
 *
 * NOT responsible for:
 *   - localStorage writes (caller does that synchronously for instant UX)
 *   - DOM theme application (caller does that synchronously)
 *   - reconciliation with server values (MeProvider handles that)
 *
 * Last-write-wins semantics: when phone A and phone B both PATCH within ~200ms,
 * the second-arriving write overwrites the first. See plan §P2-c rationale.
 */

import type { MePatchRequest } from '@sagwa/types'

const DEBOUNCE_MS = 1000
const RATE_LIMIT_PER_MIN = 30
const ENDPOINT = '/v1/me/settings'

type Result =
  | { ok: true; status: number }
  | { ok: false; reason: 'inflight' | 'rate-limited' | 'no-op' | 'network'; status?: number }

let pendingPayload: MePatchRequest | null = null
let debounceHandle: ReturnType<typeof setTimeout> | null = null
let inflight = false
let resolveQueue: Array<(r: Result) => void> = []
const callTimestamps: number[] = []

function getInitData(): string {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
  return tg?.initData ?? ''
}

function getBaseUrl(): string {
  return (
    (typeof import.meta !== 'undefined' &&
      (import.meta as ImportMeta & { env?: Record<string, string> }).env
        ?.VITE_API_BASE_URL) ||
    ''
  )
}

function withinRateBudget(): boolean {
  const now = Date.now()
  // Drop entries older than 60s
  while (callTimestamps.length > 0 && now - (callTimestamps[0] ?? 0) > 60_000) {
    callTimestamps.shift()
  }
  return callTimestamps.length < RATE_LIMIT_PER_MIN
}

async function performPatch(body: MePatchRequest): Promise<Result> {
  const url = `${getBaseUrl()}${ENDPOINT}`
  const initData = getInitData()
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(initData ? { Authorization: `tma ${initData}` } : {}),
      },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      return { ok: false, reason: 'network', status: res.status }
    }
    return { ok: true, status: res.status }
  } catch {
    return { ok: false, reason: 'network' }
  }
}

/**
 * Schedule a debounced PATCH of the given settings delta.
 *
 * Returns a promise that resolves when the PATCH lands (or is dropped). The
 * promise is shared across all callers within the same debounce window.
 */
export function syncSettings(delta: MePatchRequest): Promise<Result> {
  // No-op if nothing to write
  if (Object.keys(delta).length === 0) {
    return Promise.resolve({ ok: false, reason: 'no-op' })
  }

  // Merge with any pending payload (later toggles override earlier)
  pendingPayload = { ...(pendingPayload ?? {}), ...delta }

  if (debounceHandle !== null) clearTimeout(debounceHandle)

  return new Promise<Result>((resolve) => {
    resolveQueue.push(resolve)
    debounceHandle = setTimeout(() => {
      void flush()
    }, DEBOUNCE_MS)
  })
}

async function flush(): Promise<void> {
  debounceHandle = null
  const payload = pendingPayload
  pendingPayload = null
  const queued = resolveQueue
  resolveQueue = []

  if (payload === null || Object.keys(payload).length === 0) {
    for (const r of queued) r({ ok: false, reason: 'no-op' })
    return
  }

  if (inflight) {
    // Re-queue this payload as a pending one to be sent after current PATCH lands.
    pendingPayload = { ...(pendingPayload ?? {}), ...payload }
    for (const r of queued) r({ ok: false, reason: 'inflight' })
    return
  }

  if (!withinRateBudget()) {
    console.warn('[syncSettings] client rate-limit dropped payload', payload)
    for (const r of queued) r({ ok: false, reason: 'rate-limited' })
    return
  }

  inflight = true
  callTimestamps.push(Date.now())
  const result = await performPatch(payload)
  inflight = false
  for (const r of queued) r(result)

  // If a follow-up payload arrived during this round-trip, schedule it.
  if (pendingPayload !== null && Object.keys(pendingPayload).length > 0) {
    if (debounceHandle === null) {
      debounceHandle = setTimeout(() => {
        void flush()
      }, DEBOUNCE_MS)
    }
  }
}

/** Test-only: reset internal state. */
export function __resetSyncSettings(): void {
  pendingPayload = null
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  debounceHandle = null
  inflight = false
  resolveQueue = []
  callTimestamps.length = 0
}
